// src/api/material/extras.js
import api from "../axios";

const ensureProject = (p) => {
  if (p) return p;
  try {
    return localStorage.getItem("projectId") || null;
  } catch (_) {
    return null;
  }
};

// 여러 페이지(next) 자동 수집
async function fetchAllPages(pathOrUrl, { params } = {}) {
  let url = pathOrUrl;
  let first = true;
  const rows = [];
  while (url) {
    const res = first ? await api.get(url, { params }) : await api.get(url);
    const data = res.data;
    const page = Array.isArray(data) ? data : data?.results || [];
    rows.push(...page);
    url = data?.next || null;
    first = false;
  }
  return rows;
}

/* ===========================================================
   📌 재고 요약
=========================================================== */
export const getStockSummary = async ({ project, q, search_field } = {}) => {
  const pj = ensureProject(project);
  if (!pj) throw new Error("getStockSummary: project 필요");

  // 1) 전체 품목
  const allItems = await fetchAllPages("material/items/", {
    params: { project: pj, page_size: 200 },
  });

  // 2) 이동 전체 (필터 없음)
  const allMoves = await fetchAllPages("material/moves/", {
    params: { project: pj, page_size: 500 },
  });

  // 3) 이동합
  const qtyMap = new Map();
  let totalQty = 0;

  for (const m of allMoves) {
    const itemId = m.item ?? m.item_id;
    if (!itemId) continue;
    const delta = Number(m.qty || 0);
    qtyMap.set(itemId, (qtyMap.get(itemId) || 0) + delta);
    totalQty += delta;
  }

  // 4) 조인
  let merged = allItems.map((it) => ({
    item_id: it.id,
    item_code: it.code,
    item_name: it.name,
    unit: it.unit || "EA",
    unit_price: it.unit_price ?? 0,
    qty_on_hand: qtyMap.get(it.id) || 0,
  }));

  // 5) 검색 필터
  if (q && String(q).trim() !== "") {
    const qq = String(q).toLowerCase();

    merged = merged.filter((r) => {
      if (search_field === "code") {
        return (r.item_code || "").toLowerCase().includes(qq);
      }
      if (search_field === "name") {
        return (r.item_name || "").toLowerCase().includes(qq);
      }
      return (
        (r.item_code || "").toLowerCase().includes(qq) ||
        (r.item_name || "").toLowerCase().includes(qq)
      );
    });
  }

  // 6) 정렬 (코드 기준)
  merged.sort((a, b) =>
    String(a.item_code || "").localeCompare(String(b.item_code || ""))
  );

  // 7) 총 재고 가치 계산
  let totalValue = 0;
  for (const r of merged) {
    const qty = Number(r.qty_on_hand || 0);
    const price = Number(r.unit_price || 0);
    totalValue += qty * price;
  }

  return {
    items: merged,
    totals: { qty: totalQty, value: totalValue },
  };
};

/* ===========================================================
   📌 품목 생성
=========================================================== */
export const createMaterialItem = async ({
  project,
  code,
  name,
  spec,
  unit,
  unit_price,
}) => {
  const pj = ensureProject(project);
  if (!pj) throw new Error("createMaterialItem: project 필요");

  const payload = { code, name, spec, unit, unit_price };

  const res = await api.post("material/items/", payload, {
    params: { project: pj },
  });
  return res.data;
};

/* ===========================================================
   📌 개별 품목 입출고 이력
=========================================================== */
export const getStockHistory = async ({ project, item_id, item_code } = {}) => {
  const pj = ensureProject(project);
  if (!pj) throw new Error("getStockHistory: project 필요");

  if (!item_id && !item_code) throw new Error("item_id 또는 item_code 필요");

  const params = {
    project: pj,
    page_size: 200,
  };

  if (item_id) params.item = item_id;
  if (item_code) params.item_code = item_code;

  const rawMoves = await fetchAllPages("material/moves/", { params });

  const sorted = [...rawMoves].sort(
    (a, b) => new Date(a.move_date) - new Date(b.move_date)
  );

  let balance = 0;
  const items = sorted.map((m) => {
    const qty = Number(m.qty || 0);
    balance += qty;

    return {
      id: m.id,
      date: m.move_date,
      movement_type: qty > 0 ? "입고" : qty < 0 ? "출고" : "조정",
      doc_no: m.doc_dns || m.doc || m.related_dns || "",
      qty_change: qty,
      qty_after: balance,
      item_code: m.item_code,
      item_name: m.item_name,
      unit: m.unit,
    };
  });

  return { items };
};
